import { Injectable } from '@angular/core';
import { DBSchema, IDBPDatabase, IDBPObjectStore, IDBPTransaction, openDB } from 'idb';
import { Observable, from } from 'rxjs';

interface AppDB extends DBSchema {
  Reports: {
    key: string;
    value: unknown;
  };
}

type StorageOperation = 'setItem' | 'updateItem' | 'getItem' | 'removeItem' | 'getAllKeys' | 'clear';

export class IndexedDbOperationError extends Error {
  constructor(public readonly operation: StorageOperation, error: unknown) {
    super(error instanceof Error ? error.message : String(error), { cause: error });
    this.name = error instanceof Error ? error.name : 'IndexedDbError';
  }
}

@Injectable({
  providedIn: 'root'
})
export class IndexedDbService {

  private readonly dbName = 'SiglarReportingFormDB';
  private readonly storeName = 'Reports';
  private readonly version = 1;
  private dbPromise: Promise<IDBPDatabase<AppDB>> | null = null;

  private openDb(): Promise<IDBPDatabase<AppDB>> {
    if (!this.dbPromise) {
      const storeName = this.storeName;
      const opening = openDB<AppDB>(this.dbName, this.version, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName);
          }
        },
        terminated: () => {
          if (this.dbPromise === opening) this.dbPromise = null;
        },
      });
      this.dbPromise = opening;
      void opening.catch(() => {
        if (this.dbPromise === opening) this.dbPromise = null;
      });
    }
    return this.dbPromise;
  }

  private async run<T, Mode extends 'readonly' | 'readwrite'>(
    operation: StorageOperation,
    mode: Mode,
    callback: (store: IDBPObjectStore<AppDB, ['Reports'], 'Reports', Mode>) => Promise<T>,
  ): Promise<T> {
    for (let attempt = 0; ; attempt++) {
      let opening: Promise<IDBPDatabase<AppDB>> | null = null;
      let tx: IDBPTransaction<AppDB, ['Reports'], Mode>;
      try {
        opening = this.openDb();
        const db = await opening;
        tx = db.transaction(this.storeName, mode);
      } catch (error) {
        if (this.dbPromise === opening) this.dbPromise = null;
        // Retry only before a transaction exists, never after a draft update has begun.
        if (attempt === 0 && error instanceof DOMException &&
          (error.name === 'AbortError' || error.name === 'InvalidStateError')) {
          continue;
        }
        throw new IndexedDbOperationError(operation, error);
      }

      try {
        const result = await callback(tx.store);
        await tx.done;
        return result;
      } catch (error) {
        // A failed IndexedDB request may already have aborted the transaction.
        try { tx.abort(); } catch { /* already finished */ }
        await tx.done.catch(() => undefined);
        throw new IndexedDbOperationError(operation, error);
      }
    }
  }

  setItem<T>(key: string, value: T): Observable<string> {
    return from(
      this.run('setItem', 'readwrite', (store) => store.put(value, key))
    );
  }

  // The updater must be synchronous: only IndexedDB requests keep this transaction alive.
  updateItem<T>(key: string, update: (current: T | null) => T | undefined): Observable<T | null> {
    return from(this.run('updateItem', 'readwrite', async (store) => {
      const current = (await store.get(key) as T | undefined) ?? null;
      const next = update(current);
      if (next !== undefined) await store.put(next, key);
      return next === undefined ? current : next;
    }));
  }

  getItem<T>(key: string): Observable<T | null> {
    return from(
      this.run('getItem', 'readonly', (store) => store.get(key) as Promise<T | null>)
    );
  }

  removeItem(key: string): Observable<void> {
    return from(
      this.run('removeItem', 'readwrite', (store) => store.delete(key))
    );
  }

  getAllKeys(): Observable<string[]> {
    return from(
      this.run('getAllKeys', 'readonly', (store) => store.getAllKeys())
    );
  }

  clear(): Observable<void> {
    return from(
      this.run('clear', 'readwrite', (store) => store.clear())
    );
  }
}
